import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { Domains } from '../../constants';
import { Hasher } from '../hasher';
import { HasDependencies, hashString, Role } from '../types';
import {
  AskChappiMutation,
  ListConversationsQuery,
  ListConversationsQueryVariables,
  SortCriteriaInput,
  StartConversationMutation,
} from '../types-graphql';
import { handleGQLErrors } from '../utils';
import { ConversationId, UserPrompt } from './types';

export interface ChappiService {
  startConversation: (
    userPrompt: UserPrompt
  ) => Promise<StartConversationMutation>;
  listConversations: (
    limit: number,
    offset: number,
    createdBefore: string | null,
    createdAfter: string | null,
    sortCriteria: SortCriteriaInput[],
    conversationId: ConversationId | null
  ) => Promise<ListConversationsQuery | null>;
  askChappi: (
    userPrompt: UserPrompt,
    conversationId: ConversationId
  ) => Promise<AskChappiMutation>;
}

export class BackendChappiService implements ChappiService, HasDependencies {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly domain: Domains,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = () => {
    return [
      hashString([
        ...this.graphQlRequestService.getDeps(),
        this.domain,
        Hasher.hash(this.simulateRole),
      ]),
    ];
  };

  public startConversation = async (
    userPrompt: UserPrompt
  ): Promise<StartConversationMutation> => {
    const startResult = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.startConversation({
        domain: this.domain,
        firstQuestion: userPrompt,
      })
    );
    return startResult;
  };

  public listConversations = async (
    limit: number,
    offset: number,
    createdBefore: string | null,
    createdAfter: string | null,
    sortCriteria: SortCriteriaInput[],
    conversationId: ConversationId | null
  ): Promise<ListConversationsQuery | null> => {
    const variables: ListConversationsQueryVariables = {
      limit,
      offset,
      domainFilter: this.domain,
      initiatingRoleId: null,
      createdAfter,
      createdBefore,
      sortCriteria,
      simulateRole: null,
      conversationId,
    };

    // Use the SDK to execute the query
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.listConversations(variables)
    );
    return response;
  };

  public askChappi = async (
    userPrompt: UserPrompt,
    conversationId: ConversationId
  ): Promise<AskChappiMutation> => {
    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.askChappi({
        prompt: userPrompt,
        conversationId: conversationId,
      })
    );
    return result;
  };
}
